// Unpublished Work © 2020 Deere & Company.

import {get} from './config';

const UNAUTHORIZED = 401;
let isLoginRedirectStarted = false;

export const SESSION_USER_ID_STORAGE_KEY = 'work-planner-session-user-id';

export type SessionData = {
    id?: string;
};

type SessionResponse = SessionData & {
    expired: boolean;
};

function isSessionResponse(value: unknown): value is SessionResponse {
    return (
        typeof value === 'object' &&
        value !== null &&
        'expired' in value &&
        typeof value.expired === 'boolean' &&
        (!('id' in value) ||
            value.id === undefined ||
            typeof value.id === 'string')
    );
}

export class UnauthenticatedSessionError extends Error {
    constructor() {
        super('The session is not authenticated.');
        this.name = 'UnauthenticatedSessionError';
    }
}

export class SessionTransportError extends Error {
    constructor(cause: unknown) {
        super('Could not reach the session validation service.', {cause});
        this.name = 'SessionTransportError';
    }
}

export function shouldRedirectToLogin(error: unknown): boolean {
    return error instanceof UnauthenticatedSessionError;
}

export async function checkSession(): Promise<SessionData> {
    let response: Response;

    try {
        response = await fetch('/check-session', {
            credentials: 'include',
        });
    } catch (error) {
        throw new SessionTransportError(error);
    }

    if (response.status === UNAUTHORIZED) {
        throw new UnauthenticatedSessionError();
    }

    if (!response.ok) {
        throw new Error('Could not fetch the session from server.');
    }
    const json: unknown = await response.json();

    if (!isSessionResponse(json)) {
        throw new Error('Could not fetch the session from server.');
    }

    if (json.expired) {
        throw new UnauthenticatedSessionError();
    }

    return json.id ? {id: json.id} : {};
}

export function redirectToLoginPage(
    redirectTo = `${window.location.pathname}${window.location.search}${window.location.hash}`,
) {
    if (isLoginRedirectStarted) {
        return;
    }

    const currentUrl = encodeURIComponent(redirectTo);
    const loginUrl = `${get<string>('REACT_APP_SERVER_CORE_URL')}/login?redirectTo=${currentUrl}`;

    isLoginRedirectStarted = true;
    window.localStorage.removeItem(SESSION_USER_ID_STORAGE_KEY);
    window.location.href = loginUrl;
}
